import { create } from 'zustand'
import type { SpeechMode } from '@/utils/voiceDelivery'
import { DEFAULT_REPLY_BUDGET } from '@/constants/replyBudget'

export type PresentationMode = 'chat' | 'voice' | 'avatar'
export type VoiceProvider = 'elevenlabs' | 'azure' | 'kokoro'
export type AvatarProvider = 'tavus' | 'anam'

/** Output format options for streaming TTS (per persona) */
export type StreamingOutputFormat =
  | 'mp3_22050_32'
  | 'mp3_44100_64'
  | 'mp3_44100_96'
  | 'mp3_44100_128'
  | 'mp3_44100_192'
  | 'pcm_16000'
  | 'pcm_22050'
  | 'pcm_24000'
  | 'pcm_44100'
  | 'ulaw_8000'

export interface PronunciationRule {
  stringToReplace: string
  type: 'alias' | 'phoneme'
  alias?: string
  phoneme?: string
  alphabet?: 'ipa' | 'cmu'
}

export interface PronunciationDictionaryRef {
  dictionaryId: string
  versionId: string
  rulesHash: string
}

export interface VoiceConfig {
  provider: VoiceProvider
  voiceId: string
  voiceName?: string
  voicePreviewUrl?: string
  modelId?: string
  supportedModels?: string[]
  stability?: number
  similarityBoost?: number
  style?: number
  useSpeakerBoost?: boolean
  /** TTS speed multiplier (backend-consumed; no editor UI yet) */
  speed?: number
  /** Output format for streaming TTS (overrides global default) */
  streamingOutputFormat?: StreamingOutputFormat
  /** Pronunciation rules for ElevenLabs TTS */
  pronunciationRules?: PronunciationRule[]
  /** Cached ElevenLabs pronunciation dictionary reference */
  pronunciationDictionary?: PronunciationDictionaryRef
  /** App-level library dictionary ids (max 2 with rules, 3 without) */
  pronunciationDictionaryIds?: string[]
  /**
   * Optional STT language override for this persona. When unset, falls
   * back to the user-level setting; user fallback is 'multi'. See
   * frontend/src/constants/sttLanguages.ts for the supported set.
   */
  sttLanguage?: string
  /**
   * Display/speech split mode (spec 2026-07-28-voice-track-display-speech-split).
   * 'verbatim' (default when unset) keeps legacy behavior — spoken output
   * mirrors the displayed text. Other modes opt into the <voice> track
   * prompt addendum so voice and display consumers diverge. See
   * frontend/src/utils/voiceDelivery.ts.
   */
  speechMode?: SpeechMode
}

export type AvatarPipelineMode = 'tavus_ai' | 'anam_ai' | 'expert_rag'

export interface AvatarConfig {
  provider: AvatarProvider
  // Tavus-specific
  replicaId?: string
  // Legacy (kept for backward compat with existing Tavus personas)
  personaId?: string
  // Anam-specific
  anamPersonaId?: string       // Pre-configured Anam persona UUID
  anamAvatarId?: string        // Anam avatar UUID
  anamVoiceId?: string         // Anam voice UUID
  anamLlmId?: string           // Anam LLM UUID (or 'CUSTOMER_CLIENT_V1')
  anamSystemPrompt?: string    // System prompt for Anam's built-in LLM
  anamEnableRecording?: boolean // Session replay recording (default: true)
  anamEnableClientTools?: boolean // Client tools for UI actions
  anamLanguageCode?: string    // ISO 639-1 language code
  // Shared
  pipelineMode?: AvatarPipelineMode
  conversationId?: string      // Active conversation/session ID
}

export interface RagConfig {
  systemPrompt: string
  temperature: number
  maxTokens: number
  topK: number           // Number of sources to retrieve
  scoreThreshold: number // Minimum relevance score
  serverUrl?: string     // Optional per-persona RAG server URL (overrides global setting)
  model?: string         // Model to use for completions (optional, uses server default if not set)
  apiKey?: string        // Optional per-persona RAG server API key (overrides global setting)
  graphId?: string       // Optional graph ID for multi-graph HybridRAG; sent as X-Graph-Id header.
                         // Slug rules: ^[a-z](?:[a-z0-9-]{0,62}[a-z0-9])?$, max 64 chars.
}

/** SA-182: delta persistence — every field optional; unset inherits the
 * runtime chain (pack copy for messages, voice speed for ttsSpeed,
 * constants for timers). `*Enabled: false` silences that message. */
export interface CallingConfig {
  greetingEnabled?: boolean
  greetingMessage?: string
  goodbyeEnabled?: boolean
  goodbyeMessage?: string
  silencePromptEnabled?: boolean
  silencePromptSeconds?: number
  silencePromptMessage?: string
  silenceHangupSeconds?: number
  allowBargeIn?: boolean
  sttModel?: string
  ttsSpeed?: number
}

export interface BankTransferConfig {
  enabled: boolean
  beneficiaryName?: string
  bankName?: string
  accountOrClabe?: string
  currency: string
  instructions?: string
  detailsImage?: string
  visionModel?: string
  amountToleranceCents: number
}

export interface PaymentConfig {
  bankTransfer?: BankTransferConfig
  card?: { enabled: boolean }
}

export interface WhatsAppPersonaConfig {
  enabled: boolean
  greetingMessage?: string
  autoReply: boolean
  voiceResponse: boolean
  /** Default reply delivery when voice is enabled: both voice+text, text only,
   *  or voice only. Inert when voiceResponse is false. */
  voiceMessageMode?: 'both' | 'text' | 'voice'
  /** Seconds of silence to coalesce a burst of inbound texts into one reply.
   *  Omitted = use deployment default (WHATSAPP_BURST_DEBOUNCE_SECONDS).
   *  0 = instant (coalescing disabled for this persona). */
  burstDebounceSeconds?: number
  maxHistoryMessages: number
  sessionTimeoutHours: number
  contextBridgingEnabled: boolean
  escalationConfig?: {
    enabled: boolean
    triggerKeywords?: string[]
  }
  callingConfig?: CallingConfig
  /** IANA timezone string for inventory date calculations (e.g. 'America/Mexico_City'). */
  timezone?: string
}

export interface Persona {
  id: string
  /** Owner user ID; null on legacy rows from before user-scoping landed. */
  userId?: string
  name: string
  description?: string
  avatarUrl?: string
  presentationMode: PresentationMode
  voiceConfig?: VoiceConfig
  avatarConfig?: AvatarConfig
  ragConfig: RagConfig
  /** Current PersonaBundle id when this persona is bundle-backed (new-style);
   *  null/undefined for legacy personas. */
  currentBundleId?: string | null
  whatsappConfig?: WhatsAppPersonaConfig
  paymentConfig?: PaymentConfig
  /** True if this persona was shared by an admin (read-only for the recipient). */
  isShared?: boolean
  /** True if this persona (owned by the current user) has been shared with others. */
  hasShares?: boolean
  /** True if this persona is publicly visible to all users. */
  isPublic?: boolean
  /** True if the owner has frozen this persona — only the owner may edit it. */
  isLocked?: boolean
  /** Free-form tags for filtering. */
  tags?: string[]
  /** Source of truth for this persona — "manual" or an orchestrator slug like "auto-deploy". */
  source?: string
  /** Orchestrator-supplied identifier when source != "manual". */
  externalId?: string
  /** Orchestrator-supplied bookkeeping (workspace ID, build ID, etc.). */
  externalMetadata?: Record<string, unknown>
  /** Soft-delete timestamp (ms). Undefined when active. */
  archivedAt?: number
  createdAt: number
  updatedAt: number
}

interface PersonaState {
  // Personas are loaded from backend
  personas: Record<string, Persona>
  isLoading: boolean
  /** SA-1104: a read has settled, with the personas or with an error, so a
   * reader that shapes a page on them can tell "none yet" from "none". */
  isLoaded: boolean
  error: string | null

  // Actions (these will be called after API operations)
  setPersonas: (personas: Persona[]) => void
  addPersona: (persona: Persona) => void
  updatePersona: (id: string, updates: Partial<Persona>) => void
  deletePersona: (id: string) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
}

// Default RAG configuration
export const defaultRagConfig: RagConfig = {
  systemPrompt: 'You are a helpful AI assistant with access to a knowledge base. Answer questions accurately based on the provided context.',
  temperature: 0.7,
  maxTokens: DEFAULT_REPLY_BUDGET,
  topK: 5,
  scoreThreshold: 0.7,
}

// Default voice configurations
export const defaultVoiceConfigs: Record<VoiceProvider, Partial<VoiceConfig>> = {
  elevenlabs: {
    provider: 'elevenlabs',
    stability: 0.5,
    similarityBoost: 0.75,
    style: 0,
    useSpeakerBoost: true,
  },
  azure: {
    provider: 'azure',
  },
  kokoro: {
    provider: 'kokoro',
    voiceId: 'af_heart',
  },
}

export const usePersonaStore = create<PersonaState>()((set) => ({
  personas: {},
  isLoading: false,
  isLoaded: false,
  error: null,

  setPersonas: (personas) => {
    const personasMap = personas.reduce(
      (acc, persona) => ({ ...acc, [persona.id]: persona }),
      {} as Record<string, Persona>
    )
    set({ personas: personasMap, isLoading: false, isLoaded: true, error: null })
  },

  addPersona: (persona) => {
    set((state) => ({
      personas: { ...state.personas, [persona.id]: persona },
    }))
  },

  updatePersona: (id, updates) => {
    set((state) => {
      const persona = state.personas[id]
      if (!persona) return state

      return {
        personas: {
          ...state.personas,
          [id]: { ...persona, ...updates, updatedAt: Date.now() },
        },
      }
    })
  },

  deletePersona: (id) => {
    set((state) => {
      const { [id]: _, ...rest } = state.personas
      return { personas: rest }
    })
  },

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error, isLoading: false, isLoaded: true }),
}))
